import { useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import {
  useGetFloorsQuery,
  useCreateFloorMutation,
  useUpdateFloorMutation,
} from "../../store/api";

const Floors = () => {
  const { data: floors, isLoading, isError, error, refetch } = useGetFloorsQuery();
  const [createFloor, createState] = useCreateFloorMutation();
  const [updateFloor, updateState] = useUpdateFloorMutation();

  const [form, setForm] = useState({
    name: "",
    sort: 0,
    isActive: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [localError, setLocalError] = useState("");
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });

  const floorsList = Array.isArray(floors) ? floors : [];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError("");
    try {
      const payload = {
        ...form,
        sort: Number(form.sort),
      };
      if (editingId) {
        await updateFloor({
          floorId: editingId,
          payload,
        }).unwrap();
      } else {
        await createFloor(payload).unwrap();
      }

      if (editingId) {
        setTimeout(() => {
          updateState.reset();
          createState.reset();
          setEditingId(null);
          setForm({ name: "", sort: 0, isActive: true });
          setShowForm(false);
        }, 1000);
      } else {
        setForm({ name: "", sort: 0, isActive: true });
        setShowForm(false);
        createState.reset();
        updateState.reset();
      }
      refetch();
    } catch (err) {
      setErrorModal({
        isOpen: true,
        message: err?.data?.detail || "Failed to save floor",
      });
    }
  };

  const handleEdit = (floor) => {
    createState.reset();
    updateState.reset();
    setLocalError("");
    setEditingId(floor.floorId);
    setForm({
      name: floor.name || "",
      sort: floor.sort || 0,
      isActive: floor.isActive !== undefined ? floor.isActive : true,
    });
    setShowForm(true);
  };

  const handleToggleActive = async (floor) => {
    setLocalError("");
    try {
      await updateFloor({
        floorId: floor.floorId,
        payload: {
          name: floor.name || "",
          sort: floor.sort ?? 0,
          isActive: !floor.isActive,
        },
      }).unwrap();
      refetch();
    } catch (err) {
      setLocalError(
        err?.data?.detail ||
          err?.data?.message ||
          "Failed to update floor status.",
      );
    }
  };

  const handleCancel = () => {
    createState.reset();
    updateState.reset();
    setLocalError("");
    setEditingId(null);
    setForm({ name: "", sort: 0, isActive: true });
    setShowForm(false);
  };

  const columns = [
    { key: "name", label: "Floor Name" },
    { key: "sort", label: "Sort Order" },
    { key: "status", label: "Status" },
    { key: "actions", label: "Actions" },
  ];

  const rows = floorsList.map((floor) => {
    return {
      id: floor.floorId,
      name: floor.name,
      sort: floor.sort ?? 0,
      status: (
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {floor.isActive ? "Active" : "Inactive"}
        </span>
      ),
      actions: (
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="p-1 text-[#4f46e5] hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded transition"
            onClick={() => handleEdit(floor)}
            title="Edit"
            aria-label="Edit floor"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          <input
            type="checkbox"
            checked={Boolean(floor.isActive)}
            onChange={() => handleToggleActive(floor)}
            disabled={updateState.isLoading}
            className="h-4 w-4 cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800"
            aria-label={
              floor.isActive
                ? `Mark ${floor.name} inactive`
                : `Mark ${floor.name} active`
            }
          />
        </div>
      ),
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Floors"
        actions={
          showForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Back
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => {
                createState.reset();
                updateState.reset();
                setLocalError("");
                setEditingId(null);
                setForm({ name: "", sort: 0, isActive: true });
                setShowForm(true);
              }}
            >
              Add Floor
            </Button>
          )
        }
      />

      {showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {editingId ? "Edit Floor" : "Add Floor"}
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-3">
              <Input
                label="Floor Name"
                name="name"
                placeholder="Ground Floor"
                value={form.name}
                onChange={handleChange}
                required
              />
              <Input
                label="Sort Order"
                name="sort"
                type="number"
                placeholder="0"
                value={form.sort}
                onChange={handleChange}
                helper="Lower numbers appear first"
              />
              <Checkbox
                label="Active"
                name="isActive"
                checked={form.isActive}
                onChange={handleChange}
              />
            </div>

            {(createState.isSuccess || updateState.isSuccess) && (
              <Alert tone="success">
                Floor {editingId ? "updated" : "created"} successfully.
              </Alert>
            )}
            {(createState.isError || updateState.isError) && (
              <Alert tone="error">
                {createState.error?.data?.detail ||
                  updateState.error?.data?.detail ||
                  "Failed to save floor."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={createState.isLoading || updateState.isLoading}
              >
                {editingId ? "Update" : "Add"} Floor
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showForm && (
        <Card className="p-0">
          
          <div className="px-6 py-4">
            {localError && <Alert tone="error">{localError}</Alert>}
            {isLoading && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Loading floors...
              </p>
            )}
            {isError && (
              <Alert tone="error">
                {error?.data?.detail || "Failed to load floors."}
              </Alert>
            )}
            {!isLoading && !isError && (
              <Table
                columns={columns}
                rows={rows}
                emptyText="No floors yet. Add your first floor to get started."
              />
            )}
          </div>
        </Card>
      )}

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
        title="Error"
        confirmText="OK"
        onConfirm={() => setErrorModal({ isOpen: false, message: "" })}
      >
        <p>{errorModal.message}</p>
      </Modal>
    </div>
  );
};

export default Floors;
